import { useEffect, useState } from 'react';
import { useTenant } from '@/contexts/TenantContext';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { isExpaiApiEnabled } from '@/integrations/api/config';
import { fetchMyPermissions } from '@/integrations/api/tenants_api';

export type PermissionKey =
  | `production.${string}`
  | `quality.${string}`
  | `reports.${string}`
  | `devices.${string}`
  | `files.${string}`
  | `mlops.${string}`
  | `admin.${string}`
  | (string & {});

const FALLBACK_BY_ROLE: Record<string, (key: string) => boolean> = {
  owner: () => true,
  admin: () => true,
  supervisor: (k) =>
    k.startsWith('production.') ||
    ['quality.view', 'quality.nc.view', 'quality.nc.create', 'reports.view', 'devices.camera.view'].includes(
      k,
    ),
  operator: (k) =>
    ['production.wo.view', 'quality.nc.view', 'quality.nc.create', 'files.view'].includes(k),
};

let permissionCache: { userId: string; tenantId: string; keys: Set<string> } | null = null;
let inflight: Promise<Set<string>> | null = null;

export function clearPermissionCache() {
  permissionCache = null;
  inflight = null;
}

async function loadUserPermissions(userId: string, tenantId?: string | null): Promise<Set<string>> {
  const cacheKey = tenantId ?? '';
  if (permissionCache && permissionCache.userId === userId && permissionCache.tenantId === cacheKey) {
    return permissionCache.keys;
  }
  if (inflight) return inflight;

  inflight = (async () => {
    if (isExpaiApiEnabled()) {
      if (!tenantId) {
        const empty = new Set<string>();
        permissionCache = { userId, tenantId: cacheKey, keys: empty };
        return empty;
      }
      const apiSet = await fetchMyPermissions(tenantId);
      permissionCache = { userId, tenantId: cacheKey, keys: apiSet };
      return apiSet;
    }

    const set = new Set<string>();
    try {
      const [rolePerms, userPerms] = await Promise.all([
        supabase
          .from('role_permissions' as never)
          .select('permission_keys(key)')
          .returns<{ permission_keys: { key: string } | null }[]>(),
        supabase
          .from('user_permissions' as never)
          .select('permission_keys(key), granted')
          .eq('user_id', userId)
          .returns<{ permission_keys: { key: string } | null; granted: boolean }[]>(),
      ]);
      if (!rolePerms.error && Array.isArray(rolePerms.data)) {
        for (const row of rolePerms.data) {
          if (row.permission_keys?.key) set.add(row.permission_keys.key);
        }
      }
      if (!userPerms.error && Array.isArray(userPerms.data)) {
        for (const row of userPerms.data) {
          if (!row.permission_keys?.key) continue;
          if (row.granted) set.add(row.permission_keys.key);
          else set.delete(row.permission_keys.key);
        }
      }
    } catch {
      // ignore
    }
    permissionCache = { userId, tenantId: cacheKey, keys: set };
    return set;
  })();

  try {
    return await inflight;
  } finally {
    inflight = null;
  }
}

export function useCan(permissionKey: PermissionKey): boolean {
  const { role, currentOrg } = useTenant();
  const { role: authRole, user } = useAuth();
  const [allowed, setAllowed] = useState<boolean | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!user) {
      setAllowed(null);
      return;
    }
    void loadUserPermissions(user.id, currentOrg?.id).then((set) => {
      if (cancelled) return;
      if (isExpaiApiEnabled() && currentOrg?.id) {
        setAllowed(set.has('*') || set.has(permissionKey));
        return;
      }
      setAllowed(set.size > 0 ? set.has(permissionKey) : null);
    });
    return () => {
      cancelled = true;
    };
  }, [user, permissionKey, currentOrg?.id]);

  // Tenant super-admins (owner + admin) have full access regardless of the
  // granular permission matrix.
  if (role === 'owner' || role === 'admin') return true;
  if (allowed !== null) return allowed;

  const effectiveRole = role ?? authRole ?? null;
  if (effectiveRole && FALLBACK_BY_ROLE[effectiveRole]) {
    return FALLBACK_BY_ROLE[effectiveRole](permissionKey);
  }

  return false;
}
